/**
 * 숫자에 천 단위 콤마를 추가하는 함수
 * @param {number|string} price - 포맷팅할 가격 (숫자 또는 문자열)
 * @param {boolean} includeSymbol - 통화 기호 포함 여부 (기본값: false)
 * @param {string} symbol - 통화 기호 (기본값: '₩')
 * @param {string} locale - 로케일 설정 (기본값: 'ko-KR')
 * @returns {string} 포맷팅된 가격 문자열
 */
export const formatPrice = (price, includeSymbol = false, symbol = '₩', locale = 'ko-KR') => {
  // 입력이 undefined, null 또는 빈 문자열인 경우 처리
  if (price === undefined || price === null || price === '') {
    return includeSymbol ? `${symbol}0` : '0';
  }

  // 숫자가 아닌 경우 문자열을 숫자로 변환 시도
  let numPrice = price;
  if (typeof price !== 'number') {
    // 통화 기호 및 콤마 제거 후 숫자로 변환
    const cleanedPrice = String(price).replace(/[^0-9.-]+/g, '');
    numPrice = parseFloat(cleanedPrice);
    
    // 변환 실패 시 0 반환
    if (isNaN(numPrice)) {
      return includeSymbol ? `${symbol}0` : '0';
    }
  }

  // 천 단위 콤마 추가 (로케일에 맞게)
  const formattedPrice = numPrice.toLocaleString(locale);
  
  // 통화 기호 추가 여부에 따라 결과 반환
  return includeSymbol ? `${symbol}${formattedPrice}` : formattedPrice;
};

/**
 * 현재 언어에 맞는 통화 기호를 반환
 * @param {string} langCode - 언어 코드
 * @returns {object} 통화 기호와 로케일
 */
export const getCurrencySymbolByLanguage = (langCode) => {
  switch (langCode.toUpperCase()) {
    case 'KR':
      return { symbol: '₩', locale: 'ko-KR' };
    case 'EN':
      return { symbol: '$', locale: 'en-US' };
    case 'JA':
      return { symbol: '¥', locale: 'ja-JP' };
    case 'ZH':
      return { symbol: '¥', locale: 'zh-CN' };
    case 'VI':
      return { symbol: '₫', locale: 'vi-VN' };
    case 'TH':
      return { symbol: '฿', locale: 'th-TH' };
    case 'ID_LANG':
      return { symbol: 'Rp', locale: 'id-ID' };
    case 'HI':
      return { symbol: '₹', locale: 'hi-IN' };
    case 'MN':
      return { symbol: '₮', locale: 'mn-MN' };
    default:
      return { symbol: '₩', locale: 'ko-KR' };
  }
};

/**
 * 숫자에 천 단위 콤마를 추가하고 현재 언어에 맞는 통화 기호를 추가하는 함수
 * @param {number|string} price - 포맷팅할 가격 (숫자 또는 문자열)
 * @param {string} langCode - 언어 코드 (기본값: 'KR')
 * @returns {string} 포맷팅된 가격 문자열
 */
export const formatCurrency = (price, langCode = 'KR') => {
  const { symbol, locale } = getCurrencySymbolByLanguage(langCode);
  return formatPrice(price, true, symbol, locale);
};

/**
 * 숫자에 천 단위 콤마를 추가하고 원화 기호를 추가하는 함수
 * @param {number|string} price - 포맷팅할 가격 (숫자 또는 문자열)
 * @returns {string} 포맷팅된 가격 문자열 (예: ₩10,000)
 */
export const formatKRW = (price) => {
  return formatPrice(price, true, '₩', 'ko-KR');
};

export default formatPrice; 